import { AppBar, Dialog, DialogActions, DialogContent, Grid, Tab, Tabs, Typography } from "@mui/material";
import { blue, green, grey, orange, red, yellow } from '@mui/material/colors';
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import DashDoughnut, { DoughnutCenterText } from "components/Dashboard/DashDoughnut";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiDialogTitleClose from "components/MuiExtras/MuiDialogTitleClose";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { KnowledgeTestAnswersDistributionModel, KnowledgeTestResultAnalysisModel } from "models/ApiModels";
import * as React from "react";
import { Doughnut } from "react-chartjs-2";
import { WithTranslation, withTranslation } from "react-i18next";
import KnowledgeTestAnswersGraph from "views/KnowledgeTest/Components/KnowledgeTestAnswersGraph";
import WhoAnsweredKnowledgeTestDrawer from "views/KnowledgeTest/Dialogs/WhoAnsweredKnowledgeTestDrawer";
import { TextPluralization } from "components/TextPluralization";
import { KnowledgeTestSelectionMode } from "models/ApiEnums";
import MuiInfo from "components/MuiExtras/Typography/MuiInfo";
import { AppBarTabs } from "components/AppTabs";

/////////////////////////////////////////////////////////////////////////////////////

interface IKnowledgeTestDashboardAnswersProps {
    testId: string;
}

interface IKnowledgeTestDashboardAnswersState {
    data: KnowledgeTestAnswersDistributionModel[];
    loading: boolean;
    lastError: any;
    dialog: any;
}

type KnowledgeTestDashboardAnswersProps = IKnowledgeTestDashboardAnswersProps & WithTranslation;

class KnowledgeTestDashboardAnswers extends React.PureComponent<KnowledgeTestDashboardAnswersProps, IKnowledgeTestDashboardAnswersState> {

    constructor(props: KnowledgeTestDashboardAnswersProps) {
        super(props);
        this.state = { loading: true, data: null, dialog: null, lastError: null };
    }

    ///
    private load() {
        api.getKnowledgeTestAnswersDistribution(this.props.testId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }


    ///
    private handleWhoAnswered = () => {
        this.setState({
            dialog: (
                <WhoAnsweredKnowledgeTestDrawer
                    testId={this.props.testId}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private renderContent() {
        const { data, dialog } = this.state;
        const totalAnswers = data[0].answerSelectionMode == KnowledgeTestSelectionMode.Simple
            ? data.map(x => x.totalAnswers).reduce((p, c) => p + c)
            : data[0].totalAnswers;

        return (
            <>
                {dialog}
                <br />
                <MuiContainer>
                    <KnowledgeTestAnswersGraph data={data} />
                </MuiContainer>

                <MuiLink onClick={totalAnswers > 0 ? this.handleWhoAnswered : null} color="secondary">
                    <MuiInfo showIcon>
                        {<TextPluralization count={totalAnswers} resource="Views.Dashboards.KnowledgeTestAnswers.WhoAnsweredText" />}
                    </MuiInfo>
                </MuiLink>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public render() {
        const { loading, lastError } = this.state;

        return (
            <MuiContainer>
                {
                    loading
                        ? <SpinnerIndicator />
                        : lastError
                            ? <RequestErrorReport error={lastError} />
                            : this.renderContent()
                }
            </MuiContainer>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////

interface IKnowledgeTestDashboardTrustAndRetentionProps {
    testId: string;
}

interface IKnowledgeTestDashboardTrustAndRetentionState {
    data: KnowledgeTestResultAnalysisModel;
    loading: boolean;
    lastError: any;
}

type KnowledgeTestDashboardTrustAndRetentionProps = IKnowledgeTestDashboardTrustAndRetentionProps & WithTranslation;

class KnowledgeTestDashboardTrustAndRetention extends React.PureComponent<KnowledgeTestDashboardTrustAndRetentionProps, IKnowledgeTestDashboardTrustAndRetentionState> {

    constructor(props: KnowledgeTestDashboardTrustAndRetentionProps) {
        super(props);
        this.state = { loading: true, data: null, lastError: null };
    }

    ///
    private load() {
        api.getKnowledgeTestResultAnalysis(this.props.testId)
            .then((result) => {
                this.setState({ data: result, loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private chartTrust() {
        const { data } = this.state;
        const trustLowPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustLow / data.totalAnswers;
        //const trustMediumPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustMedium / data.totalAnswers;
        const trustHighPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHigh / data.totalAnswers;

        const centerLabel = data.totalAnswers == 0
            ? "-"
            : trustLowPercent > 30 && trustHighPercent < 15
                ? this.props.t("Views.Dashboards.Topic.TrustAndRetention.TrustLevel1")
                : trustLowPercent > 30 && trustLowPercent < 15
                    ? this.props.t("Views.Dashboards.Topic.TrustAndRetention.TrustLevel3")
                    : this.props.t("Views.Dashboards.Topic.TrustAndRetention.TrustLevel2");

        const chartData = {
            labels: [
                this.props.t("Views.Dashboards.Topic.TrustAndRetention.ChartLabel3"),
                this.props.t("Views.Dashboards.Topic.TrustAndRetention.ChartLabel2"),
                this.props.t("Views.Dashboards.Topic.TrustAndRetention.ChartLabel1")
            ],
            datasets: [{
                data: [data.trustHigh, data.trustMedium, data.trustLow, data.totalAnswers == 0 ? 100 : 0],
                backgroundColor: [green[500], yellow[500], red[500], grey[500]],
                options: {
                    elements: {
                        center: {
                            text: centerLabel,
                            color: grey.A200,
                            fontStyle: 'Arial',
                            sidePadding: 20
                        }
                    }
                }
            }]
        };

        return <Doughnut
            data={chartData}
            options={
                {
                    responsive: false,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: "right"
                        },
                        tooltip: {
                            enabled: false
                        }
                    }
                }
            }
            plugins={
                [DoughnutCenterText]
            }
        />;
    }

    ///
    private renderContent() {
        const { data } = this.state;

        const trustHighWrongPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHighWrong / data.totalAnswers;
        const trustHighCorrectPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustHighCorrect / data.totalAnswers;
        const trustLowCorrectPercent = data.totalAnswers == 0 ? 0 : 100 * data.trustLowCorrect / data.totalAnswers;
        const retentionPercent = data.retentionAnswers == null ? null : 100 * data.retentionCorrect / data.retentionAnswers;

        return (
            <>
                <MuiContainer>
                    <Grid container justifyContent="space-around" alignItems="center" >
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="black">
                                    <Typography variant="button"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.TrustChartTitle")}</b></Typography>
                                </MuiColor>
                            </div>
                            {this.chartTrust()}
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="black">
                                    <Typography variant="button"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.RetentionChartTitle")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={retentionPercent} color={blue[500]} />
                        </Grid>
                    </Grid>
                </MuiContainer>

                <MuiContainer>
                    <Grid container justifyContent="space-around" alignItems="center" >
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="error">
                                    <Typography variant="button"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutWrongLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutWrongLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustHighWrongPercent} color={red[500]} />
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="warning">
                                    <Typography variant="button"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutLowCorrectLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutLowCorrectLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustLowCorrectPercent} color={orange[500]} />
                        </Grid>
                        <Grid item>
                            <div className={GlobalClassKeys.textCenter}>
                                <MuiColor variant="success">
                                    <Typography variant="button"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutHighCorrectLabel1")}</b></Typography>
                                    <Typography variant="body2"><b>{this.props.t("Views.Dashboards.Topic.TrustAndRetention.DoughnutHighCorrectLabel2")}</b></Typography>
                                </MuiColor>
                            </div>
                            <DashDoughnut value={trustHighCorrectPercent} color={green[500]} />
                        </Grid>
                    </Grid>
                </MuiContainer>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.load();
    }

    ///
    public render() {
        const { loading, lastError } = this.state;

        return (
            <MuiContainer>
                {
                    loading
                        ? <SpinnerIndicator />
                        : lastError
                            ? <RequestErrorReport error={lastError} />
                            : this.renderContent()
                }
            </MuiContainer>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////

enum TabOptions { Answers, TrustAndRetention }

export interface IProps {
    testId: string;
    title: string;
    onClose: () => void;
}

interface IState {
    currentTab: TabOptions;
}

type Props = IProps & WithTranslation;

class KnowledgeTestDashboardDialog extends React.PureComponent<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = { currentTab: TabOptions.Answers };
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.setState({ currentTab: value });
    };

    ///
    public render() {
        const { currentTab } = this.state;
        return (
            <Dialog
                open
                fullWidth={true}
                maxWidth="md"
            >
                <MuiDialogTitleClose onClose={() => this.props.onClose()}>
                    <span>{this.props.t("Views.KnowledgeTest.Dialogs.KnowledgeTestDashboardDialog.HeaderTitle")}</span>
                    <Typography variant="body2"><MuiMuted><small>{this.props.title}</small></MuiMuted></Typography>
                </MuiDialogTitleClose>

                <DialogContent >
                    <MuiContainer margin="dense">
                        <AppBar position="static" color="primary"  >
                            <AppBarTabs centered value={currentTab} onChange={this.handleTabSelection}>
                                <Tab key={TabOptions.Answers} value={TabOptions.Answers} label={this.props.t<string>("Views.Dashboards.Topic.Tabs.TabAnswers")} />
                                <Tab key={TabOptions.TrustAndRetention} value={TabOptions.TrustAndRetention} label={this.props.t<string>("Views.Dashboards.Topic.Tabs.TabTrustAndRetention")} />
                            </AppBarTabs>
                        </AppBar>
                    </MuiContainer>

                    {
                        currentTab == TabOptions.Answers
                            ? <KnowledgeTestDashboardAnswers {...this.props} testId={this.props.testId} />
                            : <KnowledgeTestDashboardTrustAndRetention {...this.props} testId={this.props.testId} />
                    }

                </DialogContent>
                <DialogActions />
            </Dialog>
        );
    }
}

export default withTranslation()(KnowledgeTestDashboardDialog);